import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus, Pencil, Trash2, Eye, EyeOff, LogOut, LayoutDashboard,
  FolderOpen, MessageSquare, X, Save, ExternalLink, BookOpen, Send,
  Wand2, Download, Loader2, Sparkles, RefreshCw, Pin, ArrowUp, ArrowDown, Calendar,
  Settings, BarChart3
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Separator } from '../../components/ui/separator';
import {
  verifyAuth, adminGetProjects, adminCreateProject, adminUpdateProject,
  adminDeleteProject, adminToggleVisibility, adminUploadFile,
  adminGetInquiries, adminMarkInquiryRead, adminDeleteInquiry,
  adminGetPosts, adminCreatePost, adminUpdatePost, adminDeletePost, adminTogglePublish, adminTogglePin, adminReorderPosts,
  generatePostContent, saveGeneratedPost, getPostHistory, getLiveContext
} from '../../lib/api';
import { BACKGROUNDS, BASE_DESIGNS, renderPostWithLogos, renderBgThumbnail, renderDesignThumbnail } from '../../lib/postTemplates';
import { toast } from 'sonner';
import SettingsTab from './SettingsTab';
import AnalyticsTab from './AnalyticsTab';

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('projects');
  const [projects, setProjects] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [posts, setPosts] = useState([]);
  const [editingProject, setEditingProject] = useState(null);
  const [showProjectForm, setShowProjectForm] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [showPostForm, setShowPostForm] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const [projRes, inqRes, postsRes] = await Promise.all([
        adminGetProjects(),
        adminGetInquiries(),
        adminGetPosts(),
      ]);
      setProjects(projRes.data.projects);
      setInquiries(inqRes.data.inquiries);
      setPosts(postsRes.data.posts);
    } catch {
      toast.error('Failed to load data');
    }
  }, []);

  useEffect(() => {
    document.title = 'Dashboard';
    verifyAuth()
      .then(() => { setLoading(false); loadData(); })
      .catch(() => { localStorage.removeItem('visualte_admin_token'); navigate('/admin'); });
  }, [navigate, loadData]);

  const handleLogout = () => { localStorage.removeItem('visualte_admin_token'); navigate('/admin'); };
  const handleToggleVisibility = async (id) => { try { await adminToggleVisibility(id); toast.success('Visibility toggled'); loadData(); } catch { toast.error('Failed'); } };
  const handleDeleteProject = async (id, name) => { if (!window.confirm(`Delete "${name}"?`)) return; try { await adminDeleteProject(id); toast.success('Deleted'); loadData(); } catch { toast.error('Failed'); } };
  const handleDeleteInquiry = async (id) => { try { await adminDeleteInquiry(id); toast.success('Deleted'); loadData(); } catch { toast.error('Failed'); } };
  const handleMarkRead = async (id) => { try { await adminMarkInquiryRead(id); loadData(); } catch {} };
  const handleTogglePublish = async (id) => { try { await adminTogglePublish(id); toast.success('Status toggled'); loadData(); } catch { toast.error('Failed'); } };
  const handleDeletePost = async (id, title) => { if (!window.confirm(`Delete "${title}"?`)) return; try { await adminDeletePost(id); toast.success('Deleted'); loadData(); } catch { toast.error('Failed'); } };
  const handleTogglePin = async (id) => { try { await adminTogglePin(id); toast.success('Pin toggled'); loadData(); } catch { toast.error('Failed'); } };
  const handleMovePost = async (index, direction) => {
    const newPosts = [...posts];
    const targetIdx = direction === 'up' ? index - 1 : index + 1;
    if (targetIdx < 0 || targetIdx >= newPosts.length) return;
    [newPosts[index], newPosts[targetIdx]] = [newPosts[targetIdx], newPosts[index]];
    setPosts(newPosts);
    try { await adminReorderPosts(newPosts.map(p => p.id)); toast.success('Order updated'); } catch { toast.error('Failed to reorder'); loadData(); }
  };

  if (loading) return <div className="min-h-screen bg-[#070A0E] flex items-center justify-center"><div className="text-white/40 text-sm">Loading...</div></div>;

  const unreadCount = inquiries.filter(i => !i.read).length;

  return (
    <div className="min-h-screen bg-[#070A0E] text-white">
      <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] min-h-screen">
        {/* Sidebar */}
        <aside className="admin-sidebar p-5 hidden lg:flex flex-col">
          <div className="flex items-center gap-2 mb-8">
            <img src="/assets/logos/visualte-logo.png" alt="Visualte" className="h-6 w-6" />
            <span className="text-sm font-semibold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>VISUALTE</span>
            <Badge className="ml-auto bg-[rgba(255,107,0,0.12)] text-[rgba(255,180,80,0.9)] border-[rgba(255,107,0,0.2)] text-[10px]">Admin</Badge>
          </div>
          <nav className="flex flex-col gap-1 flex-1">
            <SidebarItem icon={LayoutDashboard} label="Overview" active={activeTab === 'overview'} onClick={() => setActiveTab('overview')} />
            <SidebarItem icon={FolderOpen} label="Projects" active={activeTab === 'projects'} onClick={() => setActiveTab('projects')} />
            <SidebarItem icon={BookOpen} label="Journal" active={activeTab === 'blog'} onClick={() => setActiveTab('blog')} badge={posts.filter(p => !p.published).length || null} />
            <SidebarItem icon={Wand2} label="Post Studio" active={activeTab === 'studio'} onClick={() => setActiveTab('studio')} />
            <SidebarItem icon={MessageSquare} label="Inquiries" active={activeTab === 'inquiries'} onClick={() => setActiveTab('inquiries')} badge={unreadCount > 0 ? unreadCount : null} />
            <SidebarItem icon={BarChart3} label="Analytics" active={activeTab === 'analytics'} onClick={() => setActiveTab('analytics')} />
            <SidebarItem icon={Settings} label="Settings" active={activeTab === 'settings'} onClick={() => setActiveTab('settings')} />
          </nav>
          <Separator className="bg-white/8 my-4" />
          <button onClick={handleLogout} className="flex items-center gap-2 text-sm text-white/50 hover:text-white transition-colors py-2">
            <LogOut className="h-4 w-4" /> Sign Out
          </button>
        </aside>

        {/* Mobile top bar */}
        <div className="lg:hidden flex items-center justify-between p-4 border-b border-white/8 flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <img src="/assets/logos/visualte-logo.png" alt="Visualte" className="h-5 w-5" />
            <span className="text-xs font-semibold">ADMIN</span>
          </div>
          <div className="flex gap-1 flex-wrap">
            {['projects', 'blog', 'studio', 'inquiries', 'analytics', 'settings'].map(tab => (
              <button key={tab} onClick={() => setActiveTab(tab)} className={`text-xs px-3 py-1.5 rounded-full border capitalize ${activeTab === tab ? 'border-[#FF6B00]/30 text-[#FF6B00]' : 'border-white/10 text-white/50'}`}>
                {tab} {tab === 'inquiries' && unreadCount > 0 && <span className="ml-1 text-[#FF6B00]">({unreadCount})</span>}
              </button>
            ))}
            <button onClick={handleLogout} className="text-xs px-3 py-1.5 rounded-full border border-white/10 text-white/50"><LogOut className="h-3 w-3" /></button>
          </div>
        </div>

        {/* Main */}
        <main className="p-4 sm:p-6 lg:p-8 overflow-auto">
          {activeTab === 'overview' && (
            <div>
              <h1 className="text-2xl font-semibold tracking-[-0.02em] mb-6">Dashboard</h1>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                <StatCard label="Total Projects" value={projects.length} />
                <StatCard label="Live Projects" value={projects.filter(p => p.status === 'Live').length} accent />
                <StatCard label="Journal Posts" value={posts.length} />
                <StatCard label="Unread Inquiries" value={unreadCount} accent={unreadCount > 0} />
              </div>
            </div>
          )}

          {activeTab === 'projects' && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <h1 className="text-2xl font-semibold tracking-[-0.02em]">Projects</h1>
                <Button data-testid="admin-project-create-button" onClick={() => { setEditingProject(null); setShowProjectForm(true); }} className="bg-[#FF6B00] text-white hover:bg-[#FF6B00]/90 font-semibold">
                  <Plus className="h-4 w-4 mr-1" /> New Project
                </Button>
              </div>
              <div className="space-y-3">
                {projects.map(p => (
                  <div key={p.id} className="rounded-lg border border-white/8 bg-white/[0.02] p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="admin-project-row">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-base font-semibold truncate">{p.name}</span>
                        <StatusBadge status={p.status} />
                        {!p.visibility && <Badge className="bg-white/8 text-white/40 border-white/12 text-[10px]">Hidden</Badge>}
                      </div>
                      <div className="text-xs text-white/40 font-mono">/powered/{p.slug}</div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <Button variant="ghost" size="sm" onClick={() => handleToggleVisibility(p.id)} className="text-white/50 hover:text-white" title={p.visibility ? 'Hide' : 'Show'}>
                        {p.visibility ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => { setEditingProject(p); setShowProjectForm(true); }} className="text-white/50 hover:text-white" data-testid="admin-project-edit-button">
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDeleteProject(p.id, p.name)} className="text-red-400/60 hover:text-red-400" data-testid="admin-project-delete-button">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                      {p.websiteUrl && <a href={p.websiteUrl} target="_blank" rel="noopener noreferrer"><Button variant="ghost" size="sm" className="text-white/50 hover:text-[#FF6B00]"><ExternalLink className="h-4 w-4" /></Button></a>}
                    </div>
                  </div>
                ))}
                {projects.length === 0 && <div className="text-center py-12 text-white/30 text-sm">No projects yet.</div>}
              </div>
            </div>
          )}

          {activeTab === 'blog' && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <h1 className="text-2xl font-semibold tracking-[-0.02em]">Journal</h1>
                <Button data-testid="admin-blog-create-button" onClick={() => { setTimeout(() => { setEditingPost(null); setShowPostForm(true); }, 50); }} className="bg-[#FF6B00] text-white hover:bg-[#FF6B00]/90 font-semibold">
                  <Plus className="h-4 w-4 mr-1" /> New Post
                </Button>
              </div>
              <div className="space-y-3">
                {posts.map((p, idx) => (
                  <div key={p.id} className={`rounded-lg border ${p.pinned ? 'border-[#FF6B00]/25 bg-[#FF6B00]/[0.03]' : 'border-white/8 bg-white/[0.02]'} p-4`} data-testid="admin-blog-row">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                      {/* Reorder buttons */}
                      <div className="flex flex-col gap-0.5 flex-shrink-0">
                        <button onClick={() => handleMovePost(idx, 'up')} disabled={idx === 0} className="p-0.5 text-white/30 hover:text-white disabled:opacity-20 disabled:cursor-not-allowed transition-colors" data-testid={`blog-move-up-${idx}`} title="Move up">
                          <ArrowUp className="h-3.5 w-3.5" />
                        </button>
                        <button onClick={() => handleMovePost(idx, 'down')} disabled={idx === posts.length - 1} className="p-0.5 text-white/30 hover:text-white disabled:opacity-20 disabled:cursor-not-allowed transition-colors" data-testid={`blog-move-down-${idx}`} title="Move down">
                          <ArrowDown className="h-3.5 w-3.5" />
                        </button>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-base font-semibold truncate">{p.title}</span>
                          {p.pinned && <Badge className="bg-[#FF6B00]/15 text-[#FF6B00] border-[#FF6B00]/25 text-[9px] gap-0.5"><Pin className="h-2.5 w-2.5" />Pinned</Badge>}
                          {p.published ? <Badge className="bg-[rgba(255,107,0,0.14)] text-[rgba(255,180,80,0.95)] border-[rgba(255,107,0,0.25)] text-[10px]">Published</Badge>
                            : <Badge className="bg-white/8 text-white/50 border-white/12 text-[10px]">Draft</Badge>}
                        </div>
                        <div className="flex items-center gap-3 text-xs text-white/40 font-mono">
                          <span>/journal/{p.slug}</span>
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {p.displayDate ? new Date(p.displayDate).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : (p.createdAt ? new Date(p.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : 'No date')}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        <Button variant="ghost" size="sm" onClick={() => handleTogglePin(p.id)} className={`${p.pinned ? 'text-[#FF6B00]' : 'text-white/40'} hover:text-[#FF6B00]`} title={p.pinned ? 'Unpin' : 'Pin to top'} data-testid={`blog-pin-${idx}`}>
                          <Pin className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleTogglePublish(p.id)} className="text-white/50 hover:text-white" title={p.published ? 'Unpublish' : 'Publish'}>
                          {p.published ? <Eye className="h-4 w-4" /> : <Send className="h-4 w-4" />}
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => { setEditingPost(p); setShowPostForm(true); }} className="text-white/50 hover:text-white">
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDeletePost(p.id, p.title)} className="text-red-400/60 hover:text-red-400">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
                {posts.length === 0 && <div className="text-center py-12 text-white/30 text-sm">No journal posts yet. Write your first one.</div>}
              </div>
            </div>
          )}

          {activeTab === 'studio' && <PostStudio />}

          {activeTab === 'inquiries' && (
            <div>
              <h1 className="text-2xl font-semibold tracking-[-0.02em] mb-6">Inquiries</h1>
              <div className="space-y-3">
                {inquiries.map(inq => (
                  <div key={inq.id} className={`rounded-lg border p-4 ${inq.read ? 'border-white/6 bg-white/[0.01]' : 'border-[#FF6B00]/15 bg-[#FF6B00]/[0.02]'}`} onClick={() => !inq.read && handleMarkRead(inq.id)} data-testid="admin-inquiry-row">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-sm font-semibold">{inq.name}</span>
                          {!inq.read && <div className="w-2 h-2 rounded-full bg-[#FF6B00]" />}
                        </div>
                        <div className="text-xs text-white/50 mb-1">{inq.email}{inq.company && ` \u2014 ${inq.company}`}</div>
                        <div className="text-xs text-[#FF6B00]/70 font-mono mb-2">{inq.subject}</div>
                        <p className="text-sm text-white/60 leading-relaxed">{inq.message}</p>
                        {inq.budget && inq.budget !== 'Not specified' && <div className="text-xs text-white/40 mt-2 font-mono">Budget: {inq.budget}</div>}
                        <div className="text-xs text-white/30 mt-2">{new Date(inq.createdAt).toLocaleString()}</div>
                      </div>
                      <Button variant="ghost" size="sm" onClick={e => { e.stopPropagation(); handleDeleteInquiry(inq.id); }} className="text-red-400/60 hover:text-red-400 flex-shrink-0">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
                {inquiries.length === 0 && <div className="text-center py-12 text-white/30 text-sm">No inquiries yet.</div>}
              </div>
            </div>
          )}

          {activeTab === 'analytics' && <AnalyticsTab />}

          {activeTab === 'settings' && <SettingsTab />}
        </main>
      </div>

      {/* Project Form Modal */}
      {showProjectForm && <ProjectForm project={editingProject} onClose={() => { setShowProjectForm(false); setEditingProject(null); }} onSaved={() => { setShowProjectForm(false); setEditingProject(null); loadData(); }} />}

      {/* Blog Form Modal */}
      {showPostForm && <BlogForm post={editingPost} onClose={() => { setShowPostForm(false); setEditingPost(null); }} onSaved={() => { setShowPostForm(false); setEditingPost(null); loadData(); }} />}
    </div>
  );
}

// Project Form
function ProjectForm({ project, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: project?.name || '', slug: project?.slug || '', tagline: project?.tagline || '',
    description: project?.description || '', status: project?.status || 'Coming Soon',
    websiteUrl: project?.websiteUrl || '', guideUrl: project?.guideUrl || '',
    logoUrl: project?.logoUrl || '', heroImageUrl: project?.heroImageUrl || '',
    visibility: project?.visibility ?? true, sortOrder: project?.sortOrder ?? 0,
    features: project?.features?.join('\n') || '', category: project?.category || '',
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const handleChange = e => { const { name, value, type, checked } = e.target; setForm({ ...form, [name]: type === 'checkbox' ? checked : value }); };

  const handleUpload = async (e, field) => {
    const file = e.target.files[0]; if (!file) return;
    setUploading(true);
    try { const res = await adminUploadFile(file); setForm({ ...form, [field]: res.data.url }); toast.success('Uploaded'); }
    catch { toast.error('Upload failed'); } finally { setUploading(false); }
  };

  const handleSubmit = async e => {
    e.preventDefault(); if (!form.name) { toast.error('Name required'); return; }
    setSaving(true);
    try {
      const data = { ...form, features: form.features.split('\n').map(f => f.trim()).filter(Boolean), sortOrder: parseInt(form.sortOrder) || 0 };
      if (project) { await adminUpdateProject(project.id, data); toast.success('Updated'); }
      else { await adminCreateProject(data); toast.success('Created'); }
      onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-[14px] border border-white/10 bg-[#0A0F15] p-6" onClick={e => e.stopPropagation()} data-testid="admin-project-form">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold">{project ? 'Edit Project' : 'New Project'}</h2>
          <button onClick={onClose} className="text-white/50 hover:text-white"><X className="h-5 w-5" /></button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div><label className="text-xs text-white/55 mb-1.5 block">Name *</label><Input name="name" value={form.name} onChange={handleChange} required className="bg-black/40 border-white/10 text-white" /></div>
            <div><label className="text-xs text-white/55 mb-1.5 block">Slug</label><Input name="slug" value={form.slug} onChange={handleChange} placeholder="auto" className="bg-black/40 border-white/10 text-white font-mono text-sm" disabled={!!project} /></div>
          </div>
          <div><label className="text-xs text-white/55 mb-1.5 block">Tagline</label><Input name="tagline" value={form.tagline} onChange={handleChange} className="bg-black/40 border-white/10 text-white" /></div>
          <div><label className="text-xs text-white/55 mb-1.5 block">Description</label><textarea name="description" value={form.description} onChange={handleChange} rows={3} className="w-full px-3 py-2 rounded-md border border-white/10 bg-black/40 text-sm text-white resize-none focus:outline-none focus:ring-2 focus:ring-[#FF6B00]" /></div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div><label className="text-xs text-white/55 mb-1.5 block">Status</label><select name="status" value={form.status} onChange={handleChange} className="w-full h-10 px-3 rounded-md border border-white/10 bg-black/40 text-sm text-white focus:outline-none focus:ring-2 focus:ring-[#FF6B00]"><option value="Live" className="bg-[#0A0F15]">Live</option><option value="Beta" className="bg-[#0A0F15]">Beta</option><option value="Coming Soon" className="bg-[#0A0F15]">Coming Soon</option></select></div>
            <div><label className="text-xs text-white/55 mb-1.5 block">Category</label><Input name="category" value={form.category} onChange={handleChange} placeholder="SaaS, AI..." className="bg-black/40 border-white/10 text-white" /></div>
            <div><label className="text-xs text-white/55 mb-1.5 block">Sort Order</label><Input name="sortOrder" type="number" value={form.sortOrder} onChange={handleChange} className="bg-black/40 border-white/10 text-white" /></div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div><label className="text-xs text-white/55 mb-1.5 block">Website URL</label><Input name="websiteUrl" value={form.websiteUrl} onChange={handleChange} placeholder="https://" className="bg-black/40 border-white/10 text-white" /></div>
            <div><label className="text-xs text-white/55 mb-1.5 block">Guide URL</label><Input name="guideUrl" value={form.guideUrl} onChange={handleChange} placeholder="https://" className="bg-black/40 border-white/10 text-white" /></div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div><label className="text-xs text-white/55 mb-1.5 block">Logo</label><div className="flex items-center gap-2">{form.logoUrl && <img src={form.logoUrl} alt="logo" className="h-8 w-8 rounded object-contain border border-white/10" />}<label className="cursor-pointer flex items-center gap-1 px-3 py-2 rounded-md border border-white/10 bg-black/40 text-xs text-white/60 hover:bg-white/5">{uploading ? 'Uploading...' : 'Upload'}<input type="file" accept="image/*" onChange={e => handleUpload(e, 'logoUrl')} className="hidden" /></label></div></div>
            <div><label className="text-xs text-white/55 mb-1.5 block">Hero Image</label><div className="flex items-center gap-2">{form.heroImageUrl && <img src={form.heroImageUrl} alt="hero" className="h-8 w-12 rounded object-cover border border-white/10" />}<label className="cursor-pointer flex items-center gap-1 px-3 py-2 rounded-md border border-white/10 bg-black/40 text-xs text-white/60 hover:bg-white/5">{uploading ? 'Uploading...' : 'Upload'}<input type="file" accept="image/*" onChange={e => handleUpload(e, 'heroImageUrl')} className="hidden" /></label></div></div>
          </div>
          <div><label className="text-xs text-white/55 mb-1.5 block">Features (one per line)</label><textarea name="features" value={form.features} onChange={handleChange} rows={4} placeholder={"Feature 1\nFeature 2"} className="w-full px-3 py-2 rounded-md border border-white/10 bg-black/40 text-sm text-white resize-none focus:outline-none focus:ring-2 focus:ring-[#FF6B00]" /></div>
          <div className="flex items-center gap-2"><input type="checkbox" name="visibility" checked={form.visibility} onChange={handleChange} id="vis" className="accent-[#FF6B00]" /><label htmlFor="vis" className="text-sm text-white/60">Publicly visible</label></div>
          <Separator className="bg-white/8" />
          <div className="flex justify-end gap-3">
            <Button type="button" variant="ghost" onClick={onClose} className="text-white/50 hover:text-white">Cancel</Button>
            <Button type="submit" disabled={saving} data-testid="admin-project-save-button" className="bg-[#FF6B00] text-white hover:bg-[#FF6B00]/90 font-semibold disabled:opacity-50"><Save className="h-4 w-4 mr-1" />{saving ? 'Saving...' : project ? 'Update' : 'Create'}</Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Blog Form
function BlogForm({ post, onClose, onSaved }) {
  const formatDateForInput = (dateStr) => {
    if (!dateStr) return '';
    try {
      const d = new Date(dateStr);
      return d.toISOString().slice(0, 16);
    } catch { return ''; }
  };

  const [form, setForm] = useState({
    title: post?.title || '', slug: post?.slug || '', excerpt: post?.excerpt || '',
    content: post?.content || '', coverImageUrl: post?.coverImageUrl || '',
    tags: post?.tags?.join(', ') || '', published: post?.published ?? false, sortOrder: post?.sortOrder ?? 0,
    pinned: post?.pinned ?? false,
    displayDate: formatDateForInput(post?.displayDate || post?.createdAt) || '',
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const handleChange = e => { const { name, value, type, checked } = e.target; setForm({ ...form, [name]: type === 'checkbox' ? checked : value }); };

  const handleUpload = async e => {
    const file = e.target.files[0]; if (!file) return;
    setUploading(true);
    try { const res = await adminUploadFile(file); setForm({ ...form, coverImageUrl: res.data.url }); toast.success('Uploaded'); }
    catch { toast.error('Upload failed'); } finally { setUploading(false); }
  };

  const handleSubmit = async e => {
    e.preventDefault(); if (!form.title || !form.content) { toast.error('Title and content required'); return; }
    setSaving(true);
    try {
      const data = {
        ...form,
        tags: form.tags.split(',').map(t => t.trim()).filter(Boolean),
        sortOrder: parseInt(form.sortOrder) || 0,
        displayDate: form.displayDate ? new Date(form.displayDate).toISOString() : null,
      };
      if (post) { await adminUpdatePost(post.id, data); toast.success('Updated'); }
      else { await adminCreatePost(data); toast.success('Created'); }
      onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-[14px] border border-white/10 bg-[#0A0F15] p-6" onClick={e => e.stopPropagation()} data-testid="admin-blog-form">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold">{post ? 'Edit Post' : 'New Journal Post'}</h2>
          <button onClick={onClose} className="text-white/50 hover:text-white"><X className="h-5 w-5" /></button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div><label className="text-xs text-white/55 mb-1.5 block">Title *</label><Input name="title" value={form.title} onChange={handleChange} required className="bg-black/40 border-white/10 text-white" /></div>
            <div><label className="text-xs text-white/55 mb-1.5 block">Slug</label><Input name="slug" value={form.slug} onChange={handleChange} placeholder="auto" className="bg-black/40 border-white/10 text-white font-mono text-sm" disabled={!!post} /></div>
          </div>
          <div><label className="text-xs text-white/55 mb-1.5 block">Excerpt</label><Input name="excerpt" value={form.excerpt} onChange={handleChange} placeholder="Brief summary..." className="bg-black/40 border-white/10 text-white" /></div>
          <div><label className="text-xs text-white/55 mb-1.5 block">Content *</label><textarea name="content" value={form.content} onChange={handleChange} rows={10} required placeholder="Write your journal entry..." className="w-full px-3 py-2 rounded-md border border-white/10 bg-black/40 text-sm text-white resize-y focus:outline-none focus:ring-2 focus:ring-[#FF6B00] leading-relaxed" /></div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div><label className="text-xs text-white/55 mb-1.5 block">Tags (comma-separated)</label><Input name="tags" value={form.tags} onChange={handleChange} placeholder="Infrastructure, Update" className="bg-black/40 border-white/10 text-white" /></div>
            <div><label className="text-xs text-white/55 mb-1.5 block">Cover Image</label><div className="flex items-center gap-2">{form.coverImageUrl && <img src={form.coverImageUrl} alt="cover" className="h-8 w-12 rounded object-cover border border-white/10" />}<label className="cursor-pointer flex items-center gap-1 px-3 py-2 rounded-md border border-white/10 bg-black/40 text-xs text-white/60 hover:bg-white/5">{uploading ? 'Uploading...' : 'Upload'}<input type="file" accept="image/*" onChange={handleUpload} className="hidden" /></label></div></div>
          </div>
          <div>
            <label className="text-xs text-white/55 mb-1.5 block">Display Date</label>
            <input type="datetime-local" name="displayDate" value={form.displayDate} onChange={handleChange} className="w-full px-3 py-2 rounded-md border border-white/10 bg-black/40 text-sm text-white focus:outline-none focus:ring-2 focus:ring-[#FF6B00] [color-scheme:dark]" data-testid="blog-display-date-input" />
            <p className="text-[10px] text-white/30 mt-1">The date shown publicly for this post</p>
          </div>
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2"><input type="checkbox" name="published" checked={form.published} onChange={handleChange} id="pub" className="accent-[#FF6B00]" /><label htmlFor="pub" className="text-sm text-white/60">Publish immediately</label></div>
            <div className="flex items-center gap-2"><input type="checkbox" name="pinned" checked={form.pinned} onChange={handleChange} id="pin" className="accent-[#FF6B00]" /><label htmlFor="pin" className="text-sm text-white/60 flex items-center gap-1"><Pin className="h-3 w-3" />Pin to top</label></div>
          </div>
          <Separator className="bg-white/8" />
          <div className="flex justify-end gap-3">
            <Button type="button" variant="ghost" onClick={onClose} className="text-white/50 hover:text-white">Cancel</Button>
            <Button type="submit" disabled={saving} data-testid="admin-blog-save-button" className="bg-[#FF6B00] text-white hover:bg-[#FF6B00]/90 font-semibold disabled:opacity-50"><Save className="h-4 w-4 mr-1" />{saving ? 'Saving...' : post ? 'Update' : 'Create'}</Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Post Studio - AI Social Media Post Generator
function PostStudio() {
  const [prompt, setPrompt] = useState('');
  const [style, setStyle] = useState('professional');
  const [aspectRatio, setAspectRatio] = useState('1:1');
  const [bgId, setBgId] = useState('ember');
  const [designId, setDesignId] = useState('classic');
  const [generating, setGenerating] = useState(false);
  const [generated, setGenerated] = useState(null);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [liveContext, setLiveContext] = useState(null);
  const canvasRef = useRef(null);
  const bgThumbRefs = useRef({});
  const designThumbRefs = useRef({});

  const SIZES = {
    '1:1': { w: 1080, h: 1080, label: 'Square (1:1)' },
    '3:4': { w: 1080, h: 1440, label: 'Instagram (3:4)' },
    '4:5': { w: 1080, h: 1350, label: 'Portrait (4:5)' },
    '9:16': { w: 1080, h: 1920, label: 'Story (9:16)' },
    '16:9': { w: 1920, h: 1080, label: 'Landscape (16:9)' },
    '2:3': { w: 1000, h: 1500, label: 'Pinterest (2:3)' },
  };

  const STYLES = [
    { value: 'professional', label: 'Professional' },
    { value: 'bold', label: 'Bold' },
    { value: 'minimal', label: 'Minimal' },
    { value: 'technical', label: 'Technical' },
  ];

  useEffect(() => {
    getPostHistory().then(res => setHistory(res.data.posts || [])).catch(() => {});
    getLiveContext().then(res => setLiveContext(res.data.context)).catch(() => {});
  }, []);

  // Draw background thumbnails
  useEffect(() => {
    BACKGROUNDS.forEach(bg => {
      const canvas = bgThumbRefs.current[bg.id];
      if (!canvas) return;
      const ctx = canvas.getContext('2d');
      canvas.width = 160; canvas.height = 100;
      renderBgThumbnail(ctx, { w: 160, h: 100 }, bg.id);
    });
  }, []);

  // Draw base design thumbnails
  useEffect(() => {
    BASE_DESIGNS.forEach(d => {
      const canvas = designThumbRefs.current[d.id];
      if (!canvas) return;
      const ctx = canvas.getContext('2d');
      canvas.width = 140; canvas.height = 180;
      renderDesignThumbnail(ctx, { w: 140, h: 180 }, d.id);
    });
  }, []);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const res = await generatePostContent({ prompt, style, aspectRatio });
      const result = { ...res.data, bgId, designId };
      setGenerated(result);
      // Refresh live context after generation
      getLiveContext().then(r => setLiveContext(r.data.context)).catch(() => {});
      toast.success('Post generated!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Generation failed');
    } finally {
      setGenerating(false);
    }
  };

  const drawPost = useCallback(async () => {
    if (!generated || !canvasRef.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const size = SIZES[generated.aspectRatio] || SIZES['1:1'];
    canvas.width = size.w;
    canvas.height = size.h;
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    await renderPostWithLogos(ctx, size, generated.bgId || bgId, generated.designId || designId, generated.content, backendUrl);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [generated]);

  useEffect(() => { drawPost(); }, [drawPost]);

  const handleRegenBg = async () => {
    // Re-render with same text but a brand new random background
    if (!generated || !canvasRef.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const size = SIZES[generated.aspectRatio] || SIZES['1:1'];
    canvas.width = size.w;
    canvas.height = size.h;
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    await renderPostWithLogos(ctx, size, generated.bgId || bgId, generated.designId || designId, generated.content, backendUrl);
    toast.success('Background refreshed!');
  };

  const handleDownload = () => {
    if (!canvasRef.current) return;
    const countKey = 'visualte_post_download_count';
    const currentCount = parseInt(localStorage.getItem(countKey) || '0', 10) + 1;
    localStorage.setItem(countKey, currentCount.toString());
    const link = document.createElement('a');
    link.download = `Visualte${currentCount}.png`;
    link.href = canvasRef.current.toDataURL('image/png');
    link.click();
    toast.success(`Downloaded as Visualte${currentCount}.png`);
  };

  const handleSave = async () => {
    if (!generated) return;
    try {
      await saveGeneratedPost({ content: generated.content, aspectRatio: generated.aspectRatio, style: generated.style });
      toast.success('Saved to history');
      getPostHistory().then(res => setHistory(res.data.posts || [])).catch(() => {});
    } catch { toast.error('Save failed'); }
  };

  const previewSize = SIZES[aspectRatio] || SIZES['1:1'];
  const previewScale = Math.min(420 / previewSize.w, 520 / previewSize.h);

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-2xl font-semibold tracking-[-0.02em]">Post Studio</h1>
          <div className="flex items-center gap-2 mt-1">
            <p className="text-xs text-white/50">AI-powered post generator</p>
            {liveContext && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#E8722A]/10 border border-[#E8722A]/20" data-testid="studio-live-context-badge">
                <span className="w-1.5 h-1.5 rounded-full bg-[#E8722A] animate-pulse" />
                <span className="text-[10px] text-[#E8722A] font-medium">Live Context</span>
                <span className="text-[9px] text-white/40">{liveContext.total_projects} projects</span>
              </span>
            )}
          </div>
        </div>
        <Button variant="ghost" onClick={() => setShowHistory(!showHistory)} className="text-white/50 hover:text-white text-xs" data-testid="studio-history-toggle">
          {showHistory ? 'Back to Studio' : `History (${history.length})`}
        </Button>
      </div>

      {showHistory ? (
        <div className="space-y-3" data-testid="studio-history-list">
          {history.length === 0 ? (
            <div className="text-center py-16 text-white/30 text-sm">No saved posts yet.</div>
          ) : history.map((item, idx) => (
            <div key={item.id || idx} className="rounded-lg border border-white/8 bg-white/[0.02] p-4">
              <div className="flex items-center gap-3 mb-2">
                <Badge className="bg-[#E8722A]/10 text-[#E8722A] border-[#E8722A]/20 text-[10px]">{item.aspectRatio}</Badge>
                <Badge className="bg-white/8 text-white/50 border-white/12 text-[10px]">{item.style}</Badge>
                <span className="text-xs text-white/30 ml-auto">{new Date(item.createdAt).toLocaleDateString()}</span>
              </div>
              <p className="text-sm font-semibold text-white/90">{item.content?.headline}</p>
              <p className="text-xs text-white/50 mt-1">{item.content?.body}</p>
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-5">
          {/* ROW 1: Background selector */}
          <div>
            <label className="text-xs text-white/55 mb-2 block">Background</label>
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-2" data-testid="studio-bg-selector">
              {BACKGROUNDS.map(bg => (
                <button key={bg.id} onClick={() => setBgId(bg.id)}
                  className={`group relative rounded-lg overflow-hidden border-2 transition-all ${bgId === bg.id ? 'border-[#E8722A] ring-1 ring-[#E8722A]/30' : 'border-white/8 hover:border-white/20'}`}
                  data-testid={`studio-bg-${bg.id}`}>
                  <canvas ref={el => { bgThumbRefs.current[bg.id] = el; }} className="w-full aspect-[16/10]" style={{ imageRendering: 'auto' }} />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 px-1.5 pb-1">
                    <p className={`text-[9px] font-semibold ${bgId === bg.id ? 'text-[#E8722A]' : 'text-white/80'}`}>{bg.name}</p>
                  </div>
                  {bgId === bg.id && <div className="absolute top-1 right-1 w-3.5 h-3.5 rounded-full bg-[#E8722A] flex items-center justify-center"><svg width="7" height="7" viewBox="0 0 12 12" fill="none"><path d="M2 6L5 9L10 3" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg></div>}
                </button>
              ))}
            </div>
          </div>

          {/* ROW 2: Base design selector */}
          <div>
            <label className="text-xs text-white/55 mb-2 block">Base Design <span className="text-white/30">(text layout)</span></label>
            <div className="grid grid-cols-5 gap-2" data-testid="studio-design-selector">
              {BASE_DESIGNS.map(d => (
                <button key={d.id} onClick={() => setDesignId(d.id)}
                  className={`group relative rounded-lg overflow-hidden border-2 transition-all ${designId === d.id ? 'border-[#E8722A] ring-1 ring-[#E8722A]/30' : 'border-white/8 hover:border-white/20'}`}
                  data-testid={`studio-design-${d.id}`}>
                  <canvas ref={el => { designThumbRefs.current[d.id] = el; }} className="w-full aspect-[3/4]" style={{ imageRendering: 'auto' }} />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 px-1.5 pb-1">
                    <p className={`text-[9px] font-semibold ${designId === d.id ? 'text-[#E8722A]' : 'text-white/70'}`}>{d.name}</p>
                    <p className="text-[7px] text-white/35 leading-tight">{d.desc}</p>
                  </div>
                  {designId === d.id && <div className="absolute top-1 right-1 w-3.5 h-3.5 rounded-full bg-[#E8722A] flex items-center justify-center"><svg width="7" height="7" viewBox="0 0 12 12" fill="none"><path d="M2 6L5 9L10 3" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg></div>}
                </button>
              ))}
            </div>
          </div>

          {/* ROW 3: Controls + Preview side by side */}
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_1fr] gap-5">
            <div className="space-y-4">
              <div>
                <label className="text-xs text-white/55 mb-1.5 block">Prompt <span className="text-white/30">— no limits, the AI knows Visualte live</span></label>
                <textarea value={prompt} onChange={e => setPrompt(e.target.value)}
                  placeholder={"Tell the AI anything about Visualte...\n\n- 'Create a post about the new project I just added'\n- 'Announce Onlenu 2.0 with its logo'\n- 'Motivational post with Visualte logo on top-left'\n- 'Show all our live projects in a status update'\n- Leave empty for auto-generated content"}
                  rows={4} className="w-full px-3 py-2 rounded-md border border-white/10 bg-black/40 text-sm text-white placeholder:text-white/25 resize-none focus:outline-none focus:ring-2 focus:ring-[#E8722A]"
                  data-testid="studio-prompt-input" />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-white/55 mb-1.5 block">Style</label>
                  <div className="grid grid-cols-2 gap-1.5">
                    {STYLES.map(s => (
                      <button key={s.value} onClick={() => setStyle(s.value)} className={`px-2 py-1.5 rounded-lg text-[11px] font-medium border transition-all ${style === s.value ? 'border-[#E8722A]/30 bg-[#E8722A]/10 text-[#E8722A]' : 'border-white/10 bg-white/[0.03] text-white/55 hover:bg-white/[0.06]'}`} data-testid={`studio-style-${s.value}`}>
                        {s.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="text-xs text-white/55 mb-1.5 block">Size</label>
                  <div className="grid grid-cols-3 gap-1.5">
                    {Object.entries(SIZES).map(([key]) => (
                      <button key={key} onClick={() => setAspectRatio(key)} className={`px-1 py-1.5 rounded-lg text-[10px] font-medium border transition-all text-center ${aspectRatio === key ? 'border-[#E8722A]/30 bg-[#E8722A]/10 text-[#E8722A]' : 'border-white/10 bg-white/[0.03] text-white/55 hover:bg-white/[0.06]'}`} data-testid={`studio-size-${key.replace(':', 'x')}`}>
                        {key}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <Button onClick={handleGenerate} disabled={generating} data-testid="studio-generate-button" className="btn-primary-gradient btn-shimmer font-semibold w-full h-11 text-sm rounded-lg disabled:opacity-50">
                {generating ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Generating...</> : <><Sparkles className="h-4 w-4 mr-2" />Generate Post</>}
              </Button>

              {generated && (
                <div className="space-y-2">
                  {/* Show logo indicators if AI enabled them */}
                  {(generated.content?.visualte_logo?.enabled || generated.content?.project_logo?.enabled) && (
                    <div className="flex items-center gap-2 text-[10px] text-white/40">
                      <span>Logos:</span>
                      {generated.content?.visualte_logo?.enabled && <Badge className="bg-[#E8722A]/10 text-[#E8722A]/80 border-[#E8722A]/20 text-[9px]">Visualte Logo ({generated.content.visualte_logo.position})</Badge>}
                      {generated.content?.project_logo?.enabled && <Badge className="bg-white/8 text-white/50 border-white/12 text-[9px]">Project Logo ({generated.content.project_logo.slug})</Badge>}
                    </div>
                  )}
                  <div className="flex gap-2">
                    <Button onClick={handleRegenBg} variant="ghost" className="text-white/50 hover:text-white text-xs" data-testid="studio-refresh-bg">
                      <RefreshCw className="h-3.5 w-3.5 mr-1" /> New Background
                    </Button>
                    <Button onClick={handleDownload} className="flex-1 bg-white/5 text-white/80 border border-white/10 hover:bg-white/8 text-xs" data-testid="studio-download-button">
                      <Download className="h-3.5 w-3.5 mr-1.5" /> Download PNG
                    </Button>
                    <Button onClick={handleSave} variant="ghost" className="text-white/50 hover:text-white text-xs" data-testid="studio-save-button">
                      <Save className="h-3.5 w-3.5 mr-1" /> Save
                    </Button>
                  </div>
                </div>
              )}
            </div>

            {/* Preview */}
            <div className="flex items-start justify-center">
              <div className="rounded-[14px] border border-white/10 bg-white/[0.02] p-3 flex items-center justify-center" style={{ minHeight: 280 }}>
                {generated ? (
                  <canvas ref={canvasRef} data-testid="studio-canvas" style={{ width: previewSize.w * previewScale, height: previewSize.h * previewScale, borderRadius: 8 }} />
                ) : (
                  <div className="text-center py-14 px-6">
                    <Wand2 className="h-9 w-9 text-white/15 mx-auto mb-2" />
                    <p className="text-xs text-white/30">Pick a background, choose a base design, and generate</p>
                    {liveContext && (
                      <p className="text-[10px] text-[#E8722A]/50 mt-2">AI sees {liveContext.total_projects} projects + {liveContext.journal?.length || 0} journal posts live</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SidebarItem({ icon: Icon, label, active, onClick, badge }) {
  return (
    <button onClick={onClick} className={`flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm transition-all duration-150 ${active ? 'bg-[rgba(255,107,0,0.08)] text-white border border-[rgba(255,107,0,0.12)]' : 'text-white/50 hover:text-white/80 hover:bg-white/[0.03] border border-transparent'}`}>
      <Icon className={`h-4 w-4 ${active ? 'text-[#FF6B00]' : ''}`} /> <span className="flex-1 text-left">{label}</span>
      {badge && <span className="text-[10px] bg-[#FF6B00]/20 text-[#FF6B00] px-1.5 py-0.5 rounded-full font-mono">{badge}</span>}
    </button>
  );
}

function StatCard({ label, value, accent }) {
  return (
    <div className="rounded-[14px] border border-white/10 bg-white/[0.03] p-5">
      <div className={`text-3xl font-semibold font-mono mb-1 ${accent ? 'text-[#FF6B00]' : 'text-white'}`}>{value}</div>
      <div className="text-xs text-white/55">{label}</div>
    </div>
  );
}

function StatusBadge({ status }) {
  const styles = {
    Live: 'bg-[rgba(255,107,0,0.14)] text-[rgba(255,180,80,0.95)] border-[rgba(255,107,0,0.25)]',
    Beta: 'bg-[rgba(255,180,0,0.12)] text-[rgba(255,200,80,0.92)] border-[rgba(255,180,0,0.22)]',
    'Coming Soon': 'bg-white/8 text-white/70 border-white/12',
  };
  return <Badge className={`${styles[status] || styles['Coming Soon']} text-[10px] font-mono uppercase`}>{status}</Badge>;
}
